import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import React from 'react';
import api from './lib/api';
import PwaCartNotifier from './components/PwaCartNotifier';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';

import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PopupProvider } from './contexts/PopupContext';

// Layouts
import StoreLayout from './layouts/StoreLayout';
import AdminLayout from './layouts/AdminLayout';

// Store Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';

import NotificationsPage from './pages/NotificationsPage';
import LoginPage from './pages/LoginPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ReturnsPage from './pages/ReturnsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ReturnPolicyPage from './pages/ReturnPolicyPage';
import ContactPage from './pages/ContactPage';
import ProfilePage from './pages/ProfilePage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import ShippingInfoPage from './pages/ShippingInfoPage';
import TermsPage from './pages/TermsPage';
import GlassShowcase from './components/GlassShowcase';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminProducts from './pages/admin/Products';
import AdminCategories from './pages/admin/Categories';
import AdminInventory from './pages/admin/Inventory';
import AdminInventoryStatus from './pages/admin/InventoryStatus';
import AdminOrders from './pages/admin/Orders';
import AdminQuotations from './pages/admin/Quotations';
import AdminPOS from './pages/admin/POS';
import WholesaleSales from './pages/admin/WholesaleSales';
import AdminReturns from './pages/admin/Returns';
import AdminBanners from './pages/admin/Banners';
import AdminOffers from './pages/admin/Offers';
import AdminCouriers from './pages/admin/Couriers';
import AdminShipping from './pages/admin/Shipping';
import AdminPayments from './pages/admin/Payments';
import AdminReports from './pages/admin/Reports';
import AdminSettings from './pages/admin/Settings';
import AdminPages from './pages/admin/Pages';
import AdminTeam from './pages/admin/Team';
import AdminSellerRequests from './pages/admin/SellerRequests';
import AdminPosRequests from './pages/admin/PosRequests';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, strictAdmin = false, wholesaleAllowed = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Strict Admin: Only 'admin' role allowed
  if (strictAdmin && user.role !== 'admin') {
    // If strict admin required but user is pos_user, send them to POS
    if (user.role === 'pos_user') {
      return <Navigate to="/admin/pos" replace />;
    }
    return <Navigate to="/" replace />;
  }

  // Admin Only: 'admin' or 'pos_user' allowed
  if (adminOnly && user.role !== 'admin' && user.role !== 'pos_user') {
    return <Navigate to="/" replace />;
  }

  // Wholesale Allowed: 'admin', 'pos_user', 'wholesaler' role or is_wholesale flag
  if (wholesaleAllowed && user.role !== 'admin' && user.role !== 'pos_user' && !user.is_wholesale && user.role !== 'wholesaler') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Store Routes Wrapper
const StoreRoutes = ({ children }) => (
  <StoreLayout>{children}</StoreLayout>
);

// Admin Routes Wrapper (Allows POS User)
const AdminRoutes = ({ children }) => (
  <ProtectedRoute adminOnly>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

// Strict Admin Routes Wrapper (Only Admin)
const StrictAdminRoutes = ({ children }) => (
  <ProtectedRoute strictAdmin>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

// Wholesale Routes Wrapper (Admin, POS, Wholesaler)
const WholesaleRoutes = ({ children }) => (
  <ProtectedRoute wholesaleAllowed>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

const PwaInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [show, setShow] = React.useState(false);
  const [isIos, setIsIos] = React.useState(false);
  const [showIosHelp, setShowIosHelp] = React.useState(false);

  React.useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed') === '1';
    if (dismissed) return () => { };

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) return () => { };

    const ios = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    setIsIos(ios);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShow(true);
    };

    const handleInstalled = () => {
      setShow(false);
      localStorage.setItem('pwa-install-dismissed', '1');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    if (ios) {
      setShow(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[200] rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-lg md:left-auto md:right-4 md:w-96">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">Install Amorlias Mart</div>
          <div className="text-xs text-slate-600">
            {isIos ? 'Add this app to your Home Screen for the best experience.' : 'Install the app for faster access and offline support.'}
          </div>
          {isIos && showIosHelp && (
            <div className="mt-2 text-xs text-slate-600">
              Tap the Share button in Safari, then choose “Add to Home Screen”.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {deferredPrompt && (
            <button
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90"
              onClick={async () => {
                if (!deferredPrompt) return;
                deferredPrompt.prompt();
                await deferredPrompt.userChoice;
                setDeferredPrompt(null);
                setShow(false);
                localStorage.setItem('pwa-install-dismissed', '1');
              }}
            >
              Install
            </button>
          )}
          {isIos && (
            <button
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setShowIosHelp((prev) => !prev)}
            >
              {showIosHelp ? 'Hide' : 'How'}
            </button>
          )}
          <button
            className="rounded-md border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            onClick={() => {
              setShow(false);
              localStorage.setItem('pwa-install-dismissed', '1');
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const PwaUpdateBanner = () => {
  const [registration, setRegistration] = React.useState(null);

  React.useEffect(() => {
    const handler = (event) => {
      setRegistration(event.detail?.registration || null);
    };

    window.addEventListener('pwa-update', handler);
    return () => window.removeEventListener('pwa-update', handler);
  }, []);

  if (!registration) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[200] rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-lg md:left-auto md:right-4 md:w-96">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">Update available</div>
          <div className="text-xs text-slate-600">Refresh to get the latest version.</div>
        </div>
        <button
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90"
          onClick={() => registration.waiting?.postMessage({ type: 'SKIP_WAITING' })}
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

const PwaHistoryManager = () => {
  const location = useLocation();
  const [isPop, setIsPop] = React.useState(false);

  React.useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (!standalone) return () => { };

    const onPop = () => setIsPop(true);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  React.useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (!standalone) return () => { };

    const key = `scroll:${location.pathname}${location.search}`;
    const stored = sessionStorage.getItem(key);
    if (isPop && stored) {
      window.scrollTo(0, Number(stored));
    } else {
      window.scrollTo(0, 0);
    }
    setIsPop(false);

    const onScroll = () => {
      sessionStorage.setItem(key, String(window.scrollY));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname, location.search, isPop]);

  return null;
};

function AppContent() {
  return (
    <BrowserRouter>
      <PwaHistoryManager />
      <Routes>
        {/* Store Routes */}
        <Route path="/" element={<StoreRoutes><HomePage /></StoreRoutes>} />
        <Route path="/products" element={<StoreRoutes><ProductsPage /></StoreRoutes>} />
        <Route path="/products/:id" element={<StoreRoutes><ProductDetailPage /></StoreRoutes>} />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <StoreRoutes><NotificationsPage /></StoreRoutes>
          </ProtectedRoute>
        } />
        <Route path="/login" element={<StoreRoutes><LoginPage /></StoreRoutes>} />
        <Route path="/register" element={<StoreRoutes><LoginPage /></StoreRoutes>} />
        <Route path="/checkout" element={<StoreRoutes><CheckoutPage /></StoreRoutes>} />
        <Route path="/profile" element={
          <ProtectedRoute>
            <StoreRoutes><ProfilePage /></StoreRoutes>
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute>
            <StoreRoutes><OrdersPage /></StoreRoutes>
          </ProtectedRoute>
        } />
        <Route path="/orders/:id" element={
          <ProtectedRoute>
            <StoreRoutes><OrderDetailPage /></StoreRoutes>
          </ProtectedRoute>
        } />
        <Route path="/returns" element={
          <ProtectedRoute>
            <StoreRoutes><ReturnsPage /></StoreRoutes>
          </ProtectedRoute>
        } />
        <Route path="/privacy-policy" element={<StoreRoutes><PrivacyPolicyPage /></StoreRoutes>} />
        <Route path="/return-policy" element={<StoreRoutes><ReturnPolicyPage /></StoreRoutes>} />
        <Route path="/shipping-policy" element={<StoreRoutes><ShippingPolicyPage /></StoreRoutes>} />
        <Route path="/shipping" element={<StoreRoutes><ShippingInfoPage /></StoreRoutes>} />
        <Route path="/terms" element={<StoreRoutes><TermsPage /></StoreRoutes>} />
        <Route path="/contact" element={<StoreRoutes><ContactPage /></StoreRoutes>} />
        <Route path="/glass-showcase" element={<StoreRoutes><GlassShowcase /></StoreRoutes>} />

        {/* Admin Routes */}
        <Route path="/admin" element={<StrictAdminRoutes><AdminDashboard /></StrictAdminRoutes>} />
        <Route path="/admin/products" element={<StrictAdminRoutes><AdminProducts /></StrictAdminRoutes>} />
        <Route path="/admin/categories" element={<StrictAdminRoutes><AdminCategories /></StrictAdminRoutes>} />
        <Route path="/admin/inventory" element={<StrictAdminRoutes><AdminInventory /></StrictAdminRoutes>} />
        <Route path="/admin/inventory-status" element={<StrictAdminRoutes><AdminInventoryStatus /></StrictAdminRoutes>} />
        <Route path="/admin/orders" element={<StrictAdminRoutes><AdminOrders /></StrictAdminRoutes>} />
        <Route path="/admin/shipping" element={<StrictAdminRoutes><AdminShipping /></StrictAdminRoutes>} />

        {/* POS Route - Uses AdminRoutes (Allows POS User) */}
        <Route path="/admin/pos" element={<AdminRoutes><AdminPOS /></AdminRoutes>} />
        <Route path="/admin/wholesale" element={<WholesaleRoutes><WholesaleSales /></WholesaleRoutes>} />
        <Route path="/admin/quotations" element={<StrictAdminRoutes><AdminQuotations /></StrictAdminRoutes>} />

        <Route path="/admin/returns" element={<StrictAdminRoutes><AdminReturns /></StrictAdminRoutes>} />
        <Route path="/admin/banners" element={<StrictAdminRoutes><AdminBanners /></StrictAdminRoutes>} />
        <Route path="/admin/offers" element={<StrictAdminRoutes><AdminOffers /></StrictAdminRoutes>} />
        <Route path="/admin/couriers" element={<StrictAdminRoutes><AdminCouriers /></StrictAdminRoutes>} />
        <Route path="/admin/payments" element={<StrictAdminRoutes><AdminPayments /></StrictAdminRoutes>} />
        <Route path="/admin/reports" element={<StrictAdminRoutes><AdminReports /></StrictAdminRoutes>} />
        <Route path="/admin/settings" element={<StrictAdminRoutes><AdminSettings /></StrictAdminRoutes>} />
        <Route path="/admin/pages" element={<StrictAdminRoutes><AdminPages /></StrictAdminRoutes>} />
        <Route path="/admin/team" element={<StrictAdminRoutes><AdminTeam /></StrictAdminRoutes>} />
        <Route path="/admin/seller-requests" element={<StrictAdminRoutes><AdminSellerRequests /></StrictAdminRoutes>} />
        <Route path="/admin/pos-requests" element={<StrictAdminRoutes><AdminPosRequests /></StrictAdminRoutes>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  React.useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const update = () => {
      const standalone = media.matches || window.navigator.standalone === true;
      document.body.classList.toggle('pwa-standalone', standalone);
    };

    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
    } else if (media.addListener) {
      media.addListener(update);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', update);
      } else if (media.removeListener) {
        media.removeListener(update);
      }
    };
  }, []);

  React.useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    if (!media.matches && window.navigator.standalone !== true) return;

    const prefetch = async () => {
      const endpoints = [
        '/settings/public',
        '/banners',
        '/categories',
        '/products',
        '/offers',
        '/pages',
      ];

      await Promise.allSettled(
        endpoints.map((endpoint) => api.get(endpoint)),
      );
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => prefetch(), { timeout: 2000 });
    } else {
      setTimeout(prefetch, 600);
    }
  }, []);

  return (
    <ThemeProvider>
      <HelmetProvider>
        <AuthProvider>
          <NotificationProvider>
            <CartProvider>
              <PopupProvider>
                <AppContent />
                <PwaCartNotifier />
                <PwaInstallBanner />
                <PwaUpdateBanner />
                <Toaster
                  position="top-right"
                  richColors
                  closeButton
                  toastOptions={{
                    style: {
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      color: 'hsl(var(--foreground))',
                    },
                  }}
                />
              </PopupProvider>
            </CartProvider>
          </NotificationProvider>
        </AuthProvider>
      </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;
